import { ChevronDown, ChevronRight, FileText, Loader2, SquarePen } from "lucide-react";
import { Link } from "react-router";
import {
  CHAT_SOURCE_LABELS,
  DECISION_SOURCE_LABELS,
  type ChatSource,
  type Decision,
} from "@gb/contracts";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useChatThread } from "@/features/chat/chat-api";
import { linkFor } from "@/features/chat/chat-links";
import { KIND_ICONS, sourceDateLabel } from "@/features/chat/source-card";
import { SourceGroupBlock, groupSources } from "@/features/chat/source-groups";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * One decision, in full.
 *
 * The grid card is a summary you scan; this is the thing you check when you
 * disagree with it — so it carries the rationale, what a person changed on
 * acceptance, and **what it was actually taken from**.
 */

export function DecisionDialog({
  projectId,
  decision,
  onClose,
}: {
  projectId: string;
  decision: Decision;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* `size`, not a `max-w` class — the panel's width comes from the
          variant and a max-width on top of `w-[460px]` does nothing. */}
      <DialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Decision</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          <DecisionDetail projectId={projectId} decision={decision} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The decision itself, framed by whatever is showing it.
 *
 * Shared between the dialog on the Decisions tab and the Brain entry screen —
 * a decision is one thing, and two renderings of it drift until one of them is
 * missing the rationale or the sources. The Brain screen reached exactly that
 * state: it showed the statement and neither what was asked nor what it rests
 * on.
 */
export function DecisionDetail({
  projectId,
  decision,
}: {
  projectId: string;
  decision: Decision;
}) {
  // Only for a decision elevated from an answer. Everything else was captured
  // from an email or a journal and has no structured sources to fetch —
  // `enabled` is false and the transcript below stands in.
  const question = useChatThread(projectId, decision.questionId ?? undefined);
  const sources = lastAnswerSources(question.data?.messages);

  return (
    <>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="font-display text-[0.62rem] font-semibold tracking-[0.1em] text-muted uppercase">
          {DECISION_SOURCE_LABELS[decision.source]}
        </span>
        <span className="text-[0.82rem] text-muted">
          {formatDate(decision.date)}
        </span>
      </div>

      <h3 className="font-display text-[1.35rem] leading-tight font-bold tracking-[0.01em] text-ink uppercase">
        {decision.title}
      </h3>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink">
        {decision.summary}
      </p>

      {decision.rationale && (
        <div className="mt-4 rounded-card border-[1.5px] border-border border-l-[3px] border-l-brand px-4 py-3">
          <h4 className="font-display text-[0.64rem] font-semibold tracking-[0.1em] text-brand uppercase">
            Rationale
          </h4>
          <p className="mt-1.5 text-[0.9rem] leading-relaxed text-ink">
            {decision.rationale}
          </p>
        </div>
      )}

      {decision.modificationNote && (
        // Beside the statement above, never instead of it: the statement is
        // drafted with this change (`data-model/0014` §7), and this is the
        // change in the person's own words. See `data-model/0009` §2.1.
        <div className="mt-3 rounded-card border-[1.5px] border-warn/40 bg-warn-bg/60 px-4 py-3">
          <h4 className="flex items-center gap-1.5 font-display text-[0.64rem] font-semibold tracking-[0.1em] text-warn uppercase">
            <SquarePen size={12} aria-hidden />
            Modified on acceptance
          </h4>
          <p className="mt-1.5 text-[0.9rem] leading-relaxed text-ink">
            {decision.modificationNote}
          </p>
        </div>
      )}

      {decision.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {decision.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-subtle px-3 py-1 text-[0.8rem] text-muted"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <h4 className="mt-6 mb-2.5 font-display text-[0.64rem] font-semibold tracking-[0.1em] text-muted uppercase">
        Source thread
      </h4>

      {decision.questionId && question.isPending ? (
        <div className="flex items-center gap-2 py-4 text-muted" role="status">
          <Loader2 size={15} className="animate-spin" />
          <span className="text-[0.88rem]">
            Loading what it was built from…
          </span>
        </div>
      ) : sources.length > 0 ? (
        <>
          {/* The real sources the answer cited, each still a link to the exact
              passage. This is the whole point of elevating from an answer
              rather than typing a decision: the citations survive. */}
          <div className="flex flex-col">
            {groupSources(sources).map((group) => (
              <SourceGroupBlock key={group.kind} group={group}>
                {group.items.map((source) => (
                  <SourceRow
                    key={source.n}
                    projectId={projectId}
                    source={source}
                  />
                ))}
              </SourceGroupBlock>
            ))}
          </div>
          {decision.fullThread.trim() && (
            // The question and the answer as they were. When a person changed
            // the decision, the title and the statement state the change
            // (`data-model/0014` §7), so this is where a reader sees what the
            // answer proposed. Closed, because the sources are what a reader
            // checks first. `<details>`, as `SourceGroupBlock` is.
            <details className="group mt-2">
              <summary
                className={cn(
                  "flex cursor-pointer list-none items-center gap-2 py-1.5",
                  "font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase",
                  "hover:text-ink",
                )}
              >
                <ChevronDown
                  size={13}
                  aria-hidden
                  className="shrink-0 -rotate-90 transition-transform group-open:rotate-0"
                />
                The exchange it was taken from
              </summary>
              <div className="pt-1 pb-2">
                <Transcript text={decision.fullThread} />
              </div>
            </details>
          )}
        </>
      ) : (
        // Captured from elsewhere, so there is no citable source — only the
        // material it was read out of.
        <Transcript text={decision.fullThread} />
      )}

      <p className="mt-4 rounded-card bg-subtle px-4 py-3 font-mono text-[0.8rem] text-muted">
        {decision.source === "brain" ? (
          <>Asked in Brain · “{decision.sourceLabel}”</>
        ) : (
          <>
            Captured from {DECISION_SOURCE_LABELS[decision.source]} ·{" "}
            {decision.sourceLabel}
          </>
        )}
      </p>
    </>
  );
}

/**
 * A stored exchange, verbatim and monospaced: it is a transcript, and
 * reflowing it as prose would imply somebody wrote it as prose.
 */
function Transcript({ text }: { text: string }) {
  return (
    <pre className="overflow-x-auto rounded-card border-[1.5px] border-border bg-subtle/60 px-4 py-3 font-mono text-[0.8rem] leading-relaxed whitespace-pre-wrap text-ink">
      {text}
    </pre>
  );
}

function SourceRow({
  projectId,
  source,
}: {
  projectId: string;
  source: ChatSource;
}) {
  const Icon = KIND_ICONS[source.kind] ?? FileText;
  const { href } = linkFor(projectId, source.link);
  // "Undated (uploaded …)" for an upload day, as the card says it (R87).
  const day = sourceDateLabel(source, formatDate);

  return (
    <Link
      to={href}
      className={cn(
        "flex items-center gap-3 rounded-card border-[1.5px] border-border px-4 py-3",
        "transition-colors hover:border-brand/50 hover:bg-subtle",
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-tile bg-subtle text-muted">
        <Icon size={16} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[0.94rem] font-semibold text-ink">
          {source.title}
        </span>
        <span className="block truncate text-[0.82rem] text-muted">
          {source.citation}
          {day ? ` · ${day}` : ""}
        </span>
      </span>
      <span className="sr-only">{CHAT_SOURCE_LABELS[source.kind]}</span>
      <ChevronRight size={16} aria-hidden className="shrink-0 text-brand" />
    </Link>
  );
}

/** The sources of the answer this decision was taken from. */
function lastAnswerSources(
  messages: readonly { role: string; sources: ChatSource[] }[] | undefined,
): ChatSource[] {
  if (!messages) return [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role === "assistant" && message.sources.length > 0)
      return message.sources;
  }
  return [];
}
